import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Tags, Zap, CheckCircle2, AlertTriangle, XCircle,
  Server, Database, HardDrive, Cloud, RefreshCw,
  ArrowRight, Shield, Target, Sparkles, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyDataState from '../components/EmptyDataState';

const DEMO_TAGS = {
  summary: {
    totalResources: 1847,
    tagged: 982,
    untagged: 865,
    complianceScore: 53,
    prevScore: 41,
    autoFixable: 624,
    monthlyCostUntagged: 34200,
  },
  rules: [
    { tag: 'environment', required: true, compliant: 1420, total: 1847, percent: 77 },
    { tag: 'team', required: true, compliant: 1180, total: 1847, percent: 64 },
    { tag: 'cost-center', required: true, compliant: 982, total: 1847, percent: 53 },
    { tag: 'application', required: true, compliant: 1340, total: 1847, percent: 73 },
    { tag: 'owner', required: true, compliant: 890, total: 1847, percent: 48 },
    { tag: 'project', required: false, compliant: 720, total: 1847, percent: 39 },
  ],
  untaggedResources: [
    { id: 'u1', name: 'i-0a8f3b2e4d6c', type: 'EC2 Instance', cloud: 'AWS', region: 'us-east-1', monthlyCost: 1240, missingTags: ['team', 'cost-center', 'owner'], suggestedTags: { team: 'ML Engineering', 'cost-center': 'CC-4200', owner: 'sarah.k@company.com' }, confidence: 94 },
    { id: 'u2', name: 'rds-analytics-prod', type: 'RDS Instance', cloud: 'AWS', region: 'us-west-2', monthlyCost: 2800, missingTags: ['cost-center', 'owner'], suggestedTags: { 'cost-center': 'CC-3100', owner: 'priya.m@company.com' }, confidence: 91 },
    { id: 'u3', name: 'vm-backend-worker-3', type: 'Virtual Machine', cloud: 'Azure', region: 'eastus', monthlyCost: 890, missingTags: ['team', 'cost-center', 'owner', 'project'], suggestedTags: { team: 'Backend API', 'cost-center': 'CC-2500', owner: 'alex.r@company.com', project: 'api-v3' }, confidence: 87 },
    { id: 'u4', name: 'gke-node-pool-burst', type: 'GKE Node', cloud: 'GCP', region: 'us-central1', monthlyCost: 3400, missingTags: ['cost-center', 'owner'], suggestedTags: { 'cost-center': 'CC-4200', owner: 'mike.t@company.com' }, confidence: 82 },
    { id: 'u5', name: 'ebs-vol-0f9e8d7c', type: 'EBS Volume', cloud: 'AWS', region: 'us-east-1', monthlyCost: 180, missingTags: ['team', 'cost-center', 'owner', 'application'], suggestedTags: { team: 'DevOps', 'cost-center': 'CC-1000', owner: 'james.l@company.com', application: 'infra-monitoring' }, confidence: 76 },
    { id: 'u6', name: 's3-data-lake-raw', type: 'S3 Bucket', cloud: 'AWS', region: 'us-west-2', monthlyCost: 4200, missingTags: ['owner'], suggestedTags: { owner: 'priya.m@company.com' }, confidence: 96 },
    { id: 'u7', name: 'cosmos-user-sessions', type: 'CosmosDB', cloud: 'Azure', region: 'westeurope', monthlyCost: 1650, missingTags: ['team', 'cost-center'], suggestedTags: { team: 'Platform', 'cost-center': 'CC-2000' }, confidence: 89 },
    { id: 'u8', name: 'lb-api-external', type: 'Load Balancer', cloud: 'AWS', region: 'us-east-1', monthlyCost: 340, missingTags: ['team', 'cost-center', 'owner'], suggestedTags: { team: 'Backend API', 'cost-center': 'CC-2500', owner: 'alex.r@company.com' }, confidence: 92 },
  ],
  byCloud: [
    { cloud: 'AWS', total: 1120, untagged: 520, percent: 54 },
    { cloud: 'Azure', total: 410, untagged: 198, percent: 52 },
    { cloud: 'GCP', total: 317, untagged: 147, percent: 54 },
  ],
};

const SmartTagFixer = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [fixedResources, setFixedResources] = useState([]);
  const [fixingAll, setFixingAll] = useState(false);

  useEffect(() => {
    // Iter84al — real backend; rich local demo as fallback.
    let cancelled = false;
    (async () => {
      if (!shouldShowData) { setLoading(false); return; }
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/smart-tag-fixer/scan`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) { setData(DEMO_TAGS); setLoading(false); }
      } catch (_e) {
        if (!cancelled) { setData(DEMO_TAGS); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [shouldShowData]);

  const handleFixOne = (id) => {
    setFixedResources(prev => [...prev, id]);
    toast.success('Tags applied successfully!');
  };

  const handleFixAll = () => {
    setFixingAll(true);
    setTimeout(() => {
      setFixedResources(data.untaggedResources.map(r => r.id));
      setFixingAll(false);
      toast.success(`Applied AI-suggested tags to ${data.untaggedResources.length} resources!`);
    }, 2000);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Smart Tag Fixer" description="AI auto-detects untagged cloud resources and suggests + applies correct tags. Fix 40-50% of untagged resources in one click." icon={Tags} />;
  }

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[400px]"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="smart-tag-fixer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
            <Tags className="w-7 h-7 text-emerald-400" />
            Smart Tag <span className="text-emerald-400">Fixer</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">AI auto-tags your untagged cloud resources. Fix compliance gaps in one click.</p>
        </div>
        <Button className="bg-teal-500 hover:bg-teal-600" onClick={handleFixAll} disabled={fixingAll} data-testid="fix-all-tags-btn">
          {fixingAll ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          {fixingAll ? 'Applying...' : `Auto-Fix ${data.summary.autoFixable} Resources`}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Total Resources</p>
          <p className="text-2xl font-bold text-slate-900">{data.summary.totalResources.toLocaleString()}</p>
          <p className="text-xs text-slate-400">across all clouds</p>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4">
          <p className="text-xs text-red-500 mb-1">Untagged</p>
          <p className="text-2xl font-bold text-red-600">{data.summary.untagged}</p>
          <p className="text-xs text-red-400">${data.summary.monthlyCostUntagged.toLocaleString()}/mo unaccounted</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-slate-400 mb-1">Compliance Score</p>
          <p className="text-2xl font-bold text-amber-500">{data.summary.complianceScore}%</p>
          <p className="text-xs text-emerald-500">+{data.summary.complianceScore - data.summary.prevScore}% from last week</p>
        </CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4">
          <p className="text-xs text-emerald-600 mb-1">AI Auto-Fixable</p>
          <p className="text-2xl font-bold text-emerald-700">{data.summary.autoFixable}</p>
          <p className="text-xs text-emerald-500">resources ready to tag</p>
        </CardContent></Card>
      </div>

      {/* Tag Compliance Rules */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-teal-500" />Tag Compliance by Rule</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {data.rules.map((rule, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-28 flex items-center gap-2">
                <code className="text-xs font-mono text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{rule.tag}</code>
                {rule.required && <span className="text-red-400 text-xs">*</span>}
              </div>
              <div className="flex-1"><Progress value={rule.percent} className="h-2" /></div>
              <span className={`text-sm font-medium w-12 text-right ${rule.percent > 70 ? 'text-emerald-500' : rule.percent > 50 ? 'text-amber-500' : 'text-red-500'}`}>{rule.percent}%</span>
              <span className="text-xs text-slate-400 w-28 text-right">{rule.compliant}/{rule.total}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Untagged Resources with AI Suggestions */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-500" />AI-Suggested Tags for Top Untagged Resources</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.untaggedResources.map(resource => {
            const isFixed = fixedResources.includes(resource.id);
            return (
              <div key={resource.id} className={`p-4 rounded-xl border transition-all ${isFixed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-100 hover:border-teal-300'}`} data-testid={`resource-${resource.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {isFixed ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                    <div>
                      <p className="font-medium text-slate-800">{resource.name}</p>
                      <p className="text-xs text-slate-400">{resource.type} • {resource.cloud} • {resource.region} • ${resource.monthlyCost.toLocaleString()}/mo</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-xs">{resource.confidence}% confidence</Badge>
                    {!isFixed && (
                      <Button size="sm" className="bg-teal-500 hover:bg-teal-600 h-7 text-xs" onClick={() => handleFixOne(resource.id)}>
                        <Zap className="w-3 h-3 mr-1" />Apply Tags
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 ml-8">
                  {Object.entries(resource.suggestedTags).map(([key, value]) => (
                    <span key={key} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${isFixed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                      <span className="font-medium">{key}:</span> {value}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartTagFixer;
